import React, { Component } from "react";
import PropTypes from "prop-types";
import TemplateOne from "./Templates/TemplateOne";
import Survey from "../Survey/Survey";
import MobileNumber from "./Templates/MobileNumber";
import { withTranslation } from "react-i18next";
import i18n from "../../i18n";

class Consent extends Component {
    static displayName = "Consent Component";

    static propTypes = {
        model: PropTypes.object.isRequired,
        onComplete: PropTypes.func.isRequired,
        theme: PropTypes.object,
        touchKeyboard: PropTypes.bool,
        getComponentForQuestionType: PropTypes.func,
        canAskNextQuestion: PropTypes.func,
        t: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            userAccepts: null
        };

        this.handleAcceptConsent = this.handleAcceptConsent.bind(this);
        this.handleRejectConsent = this.handleRejectConsent.bind(this);
        this.handleSurveyComplete = this.handleSurveyComplete.bind(this);
    }

    handleAcceptConsent() {
        this.props.model.userAccepted = true;
        this.setState({ userAccepts: true });

        if (!this.props.model.getSurvey()) {
            this.props.onComplete();
        }
    }

    handleRejectConsent() {
        this.props.model.userAccepted = false;
        this.setState({ userAccepts: false });
        this.props.onComplete();
    }

    handleSurveyComplete() {
        this.props.onComplete();
    }

    compositeTemplateByConsent() {
        const { t } = this.props;
        let output = null;
        let model = this.props.model;

        const isTranslationKeyFormat = str => {
            if (!str || typeof str !== "string") {
                return false;
            }
            const regex = /^[a-z0-9_.-]+(\.[a-z0-9_.-]+)+$/i;
            return regex.test(str);
        };

        const extractTextFromPTags = text => {
            if (typeof text === "string" && text.startsWith("<p>") && text.endsWith("</p>")) {
                return text.substring(3, text.length - 4);
            }
            return text;
        };

        const getTranslation = (value, fallbackKey) => {
            const extractedKey = extractTextFromPTags(value);
            const isKey = isTranslationKeyFormat(extractedKey);

            if (isKey) {
                if (i18n.exists(extractedKey)) {
                    return t(extractedKey);
                }

                if (i18n.exists(fallbackKey)) {
                    return t(fallbackKey, { defaultValue: "" });
                }

                return "";
            }

            // If there is no provided value and no translation, return empty to avoid rendering ghost buttons/links
            if ((value == null || value === "") && !i18n.exists(fallbackKey)) {
                return "";
            }

            const translation = t(fallbackKey, { defaultValue: value });

            // Avoid leaking the translation key itself when nothing is configured
            if ((value == null || value === "") && translation === fallbackKey) {
                return "";
            }

            return translation;
        };

        let schema = model.configuration?.templateOne || {};
        const lookupTranslationKey = `consents.${model.code?.toLowerCase() ?? "default"}.`;

        schema["upSell"] = getTranslation(schema["upSell"], `${lookupTranslationKey}upsell`);

        // Append bullets for customer request consents (after translation)
        if (schema["upSellBullets"]) {
            schema["upSell"] = schema["upSell"] + schema["upSellBullets"];
        }

        schema["privacyPolicy"] = getTranslation(schema["privacyPolicy"], `${lookupTranslationKey}privacy`);
        schema["termsAndConditions"] = getTranslation(schema["termsAndConditions"], `${lookupTranslationKey}tnc`);
        schema["primaryImage"] = getTranslation(schema["primaryImage"], `${lookupTranslationKey}image`);

        switch (true) {
            case model.usesTemplateOne():
                output = (
                    <TemplateOne
                        schema={schema}
                        survey={model._survey}
                        onAccepted={this.handleAcceptConsent}
                        onRejected={this.handleRejectConsent}
                    />
                );
                break;

            case model.usesTemplateMobileNumber():
                output = (
                    <MobileNumber
                        schema={model.configuration.mobileNumber}
                        survey={model.getSurvey()}
                        onAccepted={this.handleAcceptConsent}
                        onRejected={this.handleRejectConsent}
                        touchKeyboard={this.props.touchKeyboard}
                    />
                );
                break;

            default:
                throw new Error("Unsupported template [" + model.templateName + "]");
        }

        // capture the successful render of this consent
        model.shownToUser = true;

        return output;
    }

    render() {
        let output = null;
        let { model, touchKeyboard } = this.props;

        switch (this.state.userAccepts) {
            case true:
                output = (
                    <Survey
                        onComplete={this.handleSurveyComplete}
                        model={model.getSurvey()}
                        touchKeyboard={touchKeyboard}
                        getComponentForQuestionType={this.props.getComponentForQuestionType}
                        canAskNextQuestion={this.props.canAskNextQuestion}
                    />
                );
                break;
            case false:
                break;
            default:
                output = this.compositeTemplateByConsent();
        }

        return output;
    }
}

export default withTranslation()(Consent);
